import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import HomeStatusBar from '../components/home/HomeStatusBar';
import FilterChipsRow from '../components/catalog/FilterChipsRow';
import VideoPresetCard from '../components/catalog/VideoPresetCard';
import HorizontalScrollRow from '../components/catalog/HorizontalScrollRow';
import useBootstrap from '../hooks/useBootstrap';
import { useCatalog } from '../hooks/useCatalog';
import { localizedField } from '../locales/content';
import { useTranslation } from 'react-i18next';
import { getPlanLabel, resolveActivePlanKey, resolveCreditsTotal } from '../core/accountPresentation';

/**
 * VideoCatalog — страница `/video-catalog`.
 *
 * Figma source: `318:4727` gallery screen (402×809).
 *
 * Структура (figma-spec §[05.2] + Figma `363:3469` + `363:3472` review 2026-05-21):
 *  - `HomeStatusBar` (abs top) + Body (pt-30, gap-section):
 *    - `FilterChipsRow` — единый ряд chip-ов из bootstrap.rubrics.video,
 *      «Все» (системная) идёт первым. Stilistика 1-в-1 по Figma `363:3472`
 *      (см. Chip primitive: selected = surface/4 lvl, default = transparent
 *      + border 16% alpha, Raleway SemiBold 12px, letter-spacing −2%).
 *    - Стек **горизонтальных каруселей** `VideoPresetCard`-ов по секциям
 *      (аналог `HomeSection` `318:3123`): каждая секция = заголовок +
 *      горизонтальный scroll-ряд карточек w=160px (aspect 3:4 → h=240px),
 *      gap 8. Figma anatomy: `363:3478` → `363:3480` cards row.
 *    - Skeleton — те же горизонтальные ряды с заглушками 160×240.
 *    - Empty state — «Пресеты отсутствуют».
 *
 * История: ранее в ряде существовал gradient PREMIUM paywall-чип
 * (PM decision §2). По дизайн-ревью 2026-05-21 он удалён, так как Figma
 * `363:3472` его не содержит. Paywall-entry для FREE пользователей
 * остаётся через зону баланс+план в верхнем `HomeStatusBar`.
 *
 * API shape (backend commit `2138c04`, contentService.getCatalog):
 *   GET /api/content/catalog/video → { items: [{ id, name, presets: [...] }] }
 *   Section fields: `name` (not `title`), `presets` (not `items`).
 */

/** Skeleton — стек из 2 горизонтальных рядов по 6 заглушек 160×240.
 *  Имитирует реальные секции `363:3480` (cards row) во время загрузки. */
function VideoCatalogSkeleton() {
  return (
    <div className="flex flex-col gap-gap-section" data-testid="video-skeleton">
      {Array.from({ length: 2 }).map((_, sectionIdx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={sectionIdx}
          className="flex flex-col gap-gap-title-cards"
        >
          <div
            className="h-5 w-32 mx-4 rounded-home-8 skeleton-shimmer"
            style={{ '--skeleton-base': '#171719' }}
          />
          <div className="flex flex-row items-stretch gap-gap-cards overflow-x-auto scrollbar-hide px-4">
            {Array.from({ length: 6 }).map((__, cardIdx) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={cardIdx}
                className="flex-shrink-0 w-40 rounded-home-16 skeleton-shimmer"
                style={{ '--skeleton-base': '#151517', aspectRatio: '3 / 4' }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function VideoCatalog() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLocale = i18n.resolvedLanguage || i18n.language;
  const { data: bootstrap, refetch: refetchBootstrap } = useBootstrap();
  const [selectedSlug, setSelectedSlug] = useState(null);

  // «Все» (системная video-рубрика, order=0) идёт первой; далее — non-system.
  const rubrics = useMemo(() => {
    const videoRubrics = bootstrap?.rubrics?.video || [];
    const toChip = (r) => ({
      slug: r.slug,
      label: localizedField(r, 'name') || r.title || r.label,
    });
    const sys = videoRubrics.find((r) => r.isSystem);
    const rest = videoRubrics.filter((r) => !r.isSystem).map(toChip);
    return sys ? [toChip(sys), ...rest] : rest;
  }, [activeLocale, bootstrap]);

  // Initialise default selection = first rubric («Все»).
  useEffect(() => {
    if (selectedSlug == null && rubrics.length > 0) {
      setSelectedSlug(rubrics[0].slug);
    }
  }, [rubrics, selectedSlug]);

  const { items: sections, loading, refetch: refetchCatalog } = useCatalog('video', selectedSlug);
  const refetchBootstrapRef = useRef(refetchBootstrap);
  const refetchCatalogRef = useRef(refetchCatalog);

  useEffect(() => {
    refetchBootstrapRef.current = refetchBootstrap;
  }, [refetchBootstrap]);

  useEffect(() => {
    refetchCatalogRef.current = refetchCatalog;
  }, [refetchCatalog]);

  const refreshVideoCatalogData = useCallback(() => {
    // Intentional targeted refresh for manager workflows: keep the global
    // bootstrap cache policy intact, but revalidate video chips + current
    // video catalogue whenever this screen is opened or resumed.
    void refetchBootstrapRef.current?.();
    void refetchCatalogRef.current?.();
  }, []);

  useEffect(() => {
    refreshVideoCatalogData();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshVideoCatalogData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshVideoCatalogData]);

  // scroll-to-top при монтировании + смене рубрики (handoff §6).
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* jsdom does not implement scrollTo — non-fatal in tests */
      }
    }
  }, [selectedSlug]);

  const user = bootstrap?.user;
  const planLabel = getPlanLabel(
    resolveActivePlanKey({
      subscription: bootstrap?.subscription ?? null,
      user,
    }),
  );
  const credits = resolveCreditsTotal({
    credits: bootstrap?.credits ?? null,
    user,
  });

  const hasSections = sections && sections.length > 0;

  return (
    <div className="min-h-screen bg-bg-app text-white">
      <HomeStatusBar
        credits={credits}
        planLabel={planLabel}
        onProfileClick={() => navigate('/profile')}
      />

      <div className="pt-30 pb-12 flex flex-col gap-gap-section">
        {/* Filter chips row — Figma `363:3472` (tags). 1-в-1 по дизайну:
            «Все» (selected, surface/4 lvl) первой, далее non-system рубрики
            (default, border 16% alpha). Gap 8, padding-L/R 16 (через px-4),
            горизонтальный scroll. Wrapper data-testid="video-chips-row"
            нужен только для smoke-теста; сам overflow-x-auto + scrollbar-hide
            лежит внутри FilterChipsRow. */}
        <div
          className="overflow-x-auto scrollbar-hide"
          data-testid="video-chips-row"
        >
          <FilterChipsRow
            rubrics={rubrics}
            selectedSlug={selectedSlug}
            onSelect={setSelectedSlug}
          />
        </div>

        {/* Content area */}
        <div className="flex flex-col gap-gap-section">
          {loading && !hasSections && <VideoCatalogSkeleton />}

          {!loading && !hasSections && (
            <div className="text-text-mute text-center py-20 px-4">
              {t('gallery.emptyCatalog')}
            </div>
          )}

          {hasSections &&
            sections.map((section) => (
              <section
                key={section.id}
                className="flex flex-col gap-gap-title-cards"
                data-testid="video-section"
              >
                {/* section.name — real backend field (not section.title).
                    Figma `363:3479` — Raleway Bold 16px, opacity 0.9. */}
                {localizedField(section, 'name') && (
                  <h2
                    className="font-sans text-h2 text-text-primary px-4"
                    style={{ opacity: 0.9 }}
                  >
                    {localizedField(section, 'name')}
                  </h2>
                )}
                {/* Horizontal carousel row — Figma `363:3480`. Карточки 160×240,
                    gap 8, скроллятся горизонтально. -mx-4/px-4 — full-bleed
                    скролл с edge-padding (паттерн из HomeSection). */}
                <HorizontalScrollRow
                  className="flex flex-row items-stretch gap-gap-cards px-4"
                  items={section.presets || []}
                  itemWidth={160}
                  initialItemCount={4}
                  placeholderHeight={240}
                  renderItem={(item) => (
                    <div className="flex-shrink-0 w-40">
                      <VideoPresetCard preset={item} />
                    </div>
                  )}
                />
              </section>
            ))}
        </div>
      </div>
    </div>
  );
}
